import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMachine } from "@xstate/react";
import truncate from "lodash/truncate";
import { PageLayout } from "@components";
import { LoadingProgressBar } from "@rentablo/components";
import { SomethingWrong } from "../transactionWizard/steps/SomethingWrong";
import { PersistedStep as SuccessStep } from "./steps/PersistedStep";
import { useStateHelpers } from "./hooks";
import { ConfirmManualDividendStep } from "./steps/ConfirmManualDividendStep";
import { AddManualDividendStep } from "./steps/AddManualDividendStep";
import { DividendValues } from "./stateMachine/types/dividendValues";
import { editDividendWizardSM } from "./stateMachine/editDividendWizardSM";

export interface Props {
  investmentId: number;
  bookingId: number;
}

export const EditDividendWizard = ({ investmentId, bookingId }: Props) => {
  const { t } = useTranslation();
  const [currentState, sendEvent] = useMachine(editDividendWizardSM, {
    context: { bookingId },
  });
  const { isLoadingState } = useStateHelpers(currentState);

  useEffect(() => {
    sendEvent({ type: "INIT_FROM_INVESTMENT", investmentId });
  }, [investmentId, sendEvent]);

  const investmentName = currentState.context.investment?.name ?? "";
  const handleBackStep = () => sendEvent({ type: "BACK" });

  return (
    <PageLayout
      breadcrumbs={[
        { to: "/", text: t("breadcrumbs.home") },
        { to: "/investments", text: t("breadcrumbs.investments") },
        { to: `/investments/${investmentId}`, text: truncate(investmentName) },
        { text: t("editDividendWizard.title") },
      ]}
    >
      {isLoadingState() && <LoadingProgressBar />}

      {currentState.matches("editManualDividend") && (
        <AddManualDividendStep
          onNext={(dividendValues: DividendValues) =>
            sendEvent({
              type: "DIVIDEND_FILLED",
              dividendValues,
            })
          }
          dividendValues={currentState.context.dividendValues}
          investmentName={currentState.context.investment?.name ?? ""}
          investmentCurrency={
            currentState.context.investment?.currency ?? "EUR"
          }
          validationErrors={currentState.context.validationErrors ?? []}
        />
      )}

      {currentState.matches("summary") && (
        <ConfirmManualDividendStep
          dividendValues={currentState.context.dividendValues}
          investmentCurrency={
            currentState.context.investment?.currency ?? "EUR"
          }
          investmentName={currentState.context.investment?.name ?? ""}
          onPrevious={handleBackStep}
          onNext={() => sendEvent({ type: "PERSIST" })}
        />
      )}

      {currentState.matches("success") && (
        <SuccessStep
          investmentName={currentState.context.investment?.name || ""}
          redirectUrl={`/investments/${investmentId}?tab=dividends`}
        />
      )}

      {currentState.matches("somethingWrong") && (
        <SomethingWrong message={currentState.context.error?.message} />
      )}
    </PageLayout>
  );
};
